import api from './api'

const profileService = {
  /**
   * Get all profiles for current account
   */
  getProfiles: async () => {
    const response = await api.get('/profiles')
    return response.data
  },

  /**
   * Get a specific profile
   */
  getProfile: async (id) => {
    const response = await api.get(`/profiles/${id}`)
    return response.data
  },

  /**
   * Create a new profile
   */
  createProfile: async (data) => {
    const response = await api.post('/profiles', data)
    return response.data
  },

  /**
   * Update a profile
   */
  updateProfile: async (id, data) => {
    const response = await api.put(`/profiles/${id}`, data)
    return response.data
  },

  /**
   * Delete a profile
   */
  deleteProfile: async (id) => {
    const response = await api.delete(`/profiles/${id}`)
    return response.data
  },

  /**
   * Clone a profile
   */
  cloneProfile: async (id) => {
    const response = await api.post(`/profiles/${id}/clone`)
    return response.data
  },

  /**
   * Get scores for a profile
   */
  getScores: async (profileId, params = {}) => {
    const response = await api.get(`/profiles/${profileId}/scores`, { params })
    return response.data
  },

  /**
   * Add a score
   */
  addScore: async (profileId, data) => {
    const response = await api.post(`/profiles/${profileId}/scores`, data)
    return response.data
  },

  /**
   * Update a score
   */
  updateScore: async (profileId, scoreId, data) => {
    const response = await api.put(`/profiles/${profileId}/scores/${scoreId}`, data)
    return response.data
  },

  /**
   * Delete a score
   */
  deleteScore: async (profileId, scoreId) => {
    const response = await api.delete(`/profiles/${profileId}/scores/${scoreId}`)
    return response.data
  },

  /**
   * Get pending scores (from browser extension)
   */
  getPendingScores: async (profileId) => {
    const response = await api.get(`/profiles/${profileId}/scores/pending`)
    return response.data
  },

  /**
   * Get categories for a profile
   */
  getCategories: async (profileId) => {
    const response = await api.get(`/profiles/${profileId}/categories`)
    return response.data
  },

  /**
   * Create a category
   */
  createCategory: async (profileId, data) => {
    const response = await api.post(`/profiles/${profileId}/categories`, data)
    return response.data
  },

  /**
   * Update a category
   */
  updateCategory: async (profileId, categoryId, data) => {
    const response = await api.put(`/profiles/${profileId}/categories/${categoryId}`, data)
    return response.data
  },

  /**
   * Delete a category
   */
  deleteCategory: async (profileId, categoryId) => {
    const response = await api.delete(`/profiles/${profileId}/categories/${categoryId}`)
    return response.data
  },

  /**
   * Trust a profile
   */
  trustProfile: async (profileId, data) => {
    const response = await api.post(`/profiles/${profileId}/trust/profiles`, data)
    return response.data
  },

  /**
   * Trust a network
   */
  trustNetwork: async (profileId, data) => {
    const response = await api.post(`/profiles/${profileId}/trust/networks`, data)
    return response.data
  },

  /**
   * Untrust (remove trust relationship)
   */
  untrust: async (profileId, type, targetId) => {
    const response = await api.delete(`/profiles/${profileId}/trust/${type}/${targetId}`)
    return response.data
  },

  /**
   * Get trust relationships
   */
  getTrustRelationships: async (profileId, targetType = null) => {
    const params = targetType ? { target_type: targetType } : {}
    const response = await api.get(`/profiles/${profileId}/trust`, { params })
    return response.data
  },

  /**
   * Update a trust relationship
   */
  updateTrustRelationship: async (profileId, categoryId, data) => {
    const response = await api.put(`/profiles/${profileId}/trust/${categoryId}`, data)
    return response.data
  },

  // Subordinate Profile Management

  /**
   * Get subordinate profiles for a parent profile
   */
  getSubordinates: async (profileId) => {
    const response = await api.get(`/profiles/${profileId}/subordinates`)
    return response.data
  },

  /**
   * Add a subordinate profile
   */
  addSubordinate: async (profileId, subordinateId) => {
    const response = await api.post(`/profiles/${profileId}/subordinates`, {
      subordinate_id: subordinateId
    })
    return response.data
  },

  /**
   * Remove a subordinate profile
   */
  removeSubordinate: async (profileId, subordinateId) => {
    const response = await api.delete(`/profiles/${profileId}/subordinates/${subordinateId}`)
    return response.data
  },

  /**
   * Get search history for all subordinate profiles
   */
  getSubordinateSearchHistory: async (profileId, subordinateId = null, limit = 50) => {
    const params = { limit }
    if (subordinateId) {
      params.subordinate_id = subordinateId
    }
    const response = await api.get(`/profiles/${profileId}/subordinates/search-history`, { params })
    return response.data
  },

  // Parent PIN Management

  /**
   * Set or update the parent PIN for a child profile
   */
  setParentPin: async (profileId, pin) => {
    const response = await api.put(`/profiles/${profileId}/parent-pin`, { pin })
    return response.data
  },

  /**
   * Verify the parent PIN for a child profile
   */
  verifyParentPin: async (profileId, pin) => {
    const response = await api.post(`/profiles/${profileId}/parent-pin/verify`, { pin })
    return response.data
  },

  /**
   * Remove the parent PIN from a child profile
   */
  removeParentPin: async (profileId) => {
    const response = await api.delete(`/profiles/${profileId}/parent-pin`)
    return response.data
  },
}

export default profileService
